// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@sc/App/Components/Common/XngZone";
import { checkoutPages } from "@sc/XDL/constants";
import { mergeWithCss } from "@sc/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    // XNG-Changes
    container?: InjectableCss;
    topSection?: InjectableCss;
    middleSection?: InjectableCss;
    bottomSection?: InjectableCss;
    logo?: InjectableCss;
    socialLinks?: InjectableCss;
    applicationWrapper?: InjectableCss;
    contentBlockWrapper?: InjectableCss;
    contentBlock?: InjectableCss;
    badges?: InjectableCss;
    linkListWrapper?: InjectableCss;
    linkList?: InjectableCss;
    copyRightWrapper?: InjectableCss;
    copyRight?: TypographyProps;
    siteMapLinksWrapper?: InjectableCss;
}

export const footerContainerStyles: FooterContainerStyles = {
    logoGridItem: {
        width: 12,
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            img {
                height: 78px;
            }
        `,
    },
    linkList1GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList2GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList3GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList4GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    subscribeGridItem: {
        width: [12, 12, 8, 4, 4],
    },
    socialLinksGridItem: {
        width: 12,
    },
    siteMapLinksGridItem: {
        width: 12,
    },
    richContentGridItem: {
        width: 12,
    },
    logoWrapper: {
        css: css`
            width: 110px;
        `,
    },
};

const FooterContainer: FC<OwnProps> = ({ id, fields, extendedStyles }) => {
    const [styles] = React.useState(() => mergeWithCss(footerContainerStyles, extendedStyles));

    // Xng-Changes
    const { isCheckoutPage } = useAppSelector(state => ({
        isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
    }));

    // Xng-Changes
    const copyrightMessage = () => {
        const message = siteMessage("Custom_Footer_Copyright") as string;
        return message.replace("{0}", String(new Date().getFullYear()));
    };

    // XNG-Changes: Update Structure
    return (
        <StyledWrapper {...styles.container} {...{ isCheckoutPage }}>
            {!isCheckoutPage && (
                <>
                    <StyledWrapper {...styles.topSection}>
                        <StyledWrapper {...styles.logoWrapper}>
                            <StyledWrapper {...styles.logo}>
                                <XngZone zoneName="Logo" contentId={id} hint="Logo" />
                            </StyledWrapper>
                            <StyledWrapper {...styles.socialLinks}>
                                <XngZone zoneName="SocialLinks" contentId={id} hint="Social Links" />
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.applicationWrapper}>
                            <XngZone zoneName="Application" contentId={id} hint="Application Section" />
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.middleSection}>
                        <StyledWrapper {...styles.contentBlockWrapper}>
                            <StyledWrapper {...styles.contentBlock}>
                                <XngZone zoneName="Description" contentId={id} hint="Description" />
                            </StyledWrapper>
                            <StyledWrapper {...styles.badges}>
                                <XngZone zoneName="Badges" contentId={id} hint="Badges" />
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.linkListWrapper}>
                            <StyledWrapper {...styles.linkList}>
                                <XngZone zoneName="LinkList1" contentId={id} hint="Resources Pages" />
                            </StyledWrapper>
                            <StyledWrapper {...styles.linkList}>
                                <XngZone zoneName="LinkList2" contentId={id} hint="Company Pages" />
                            </StyledWrapper>
                            <StyledWrapper {...styles.linkList}>
                                <XngZone zoneName="LinkList3" contentId={id} hint="Support Pages" />
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </>
            )}
            <StyledWrapper {...styles.bottomSection}>
                <StyledWrapper {...styles.copyRightWrapper}>
                    <Typography {...styles.copyRight}>{copyrightMessage()}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.siteMapLinksWrapper}>
                    <XngZone zoneName="SiteMapLinks" contentId={id} hint="Terms & Conditions & Privacy Policy" />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
